// Feature Flags Configuration
// This file manages feature flags for both retail and admin frontends

import { getApiBaseUrl, getWsBaseUrl } from '../utils/apiConfig';

// Utility function to parse boolean environment variables
const parseBool = (value: string | undefined, defaultValue: boolean = false): boolean => {
  if (value === undefined) return defaultValue;
  return value.toLowerCase() === 'true';
};

// Utility function to parse number environment variables
const parseNumber = (value: string | undefined, defaultValue: number): number => {
  if (value === undefined) return defaultValue;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

// Feature flags configuration
export const featureFlags = {
  retail: {
    enableTokenRefreshTest: parseBool(process.env.REACT_APP_RETAIL_ENABLE_TOKEN_REFRESH_TEST, false),
    enableDebugPanels: parseBool(process.env.REACT_APP_RETAIL_ENABLE_DEBUG_PANELS, false),
    enableInactivityTimeout: parseBool(process.env.REACT_APP_RETAIL_ENABLE_INACTIVITY_TIMEOUT, true),
    enableTrueLayerRetail: parseBool(process.env.REACT_APP_RETAIL_ENABLE_TRUELAYER_RETAIL, false),
    enableTrueLayerDashboardPaymentsWidget: parseBool(
      process.env.REACT_APP_RETAIL_ENABLE_TRUELAYER_DASHBOARD_PAYMENTS_WIDGET,
      true
    ),
    enableTrueLayerPaymentsPageWidget: parseBool(
      process.env.REACT_APP_RETAIL_ENABLE_TRUELAYER_PAYMENTS_PAGE_WIDGET,
      true
    ),
    enableTrueLayerKuCoinTopup: parseBool(
      process.env.REACT_APP_RETAIL_ENABLE_TRUELAYER_KUCOIN_TOPUP,
      false
    ),
  },
  admin: {
    enableTokenRefreshTest: parseBool(process.env.REACT_APP_ADMIN_ENABLE_TOKEN_REFRESH_TEST, false),
    enableDebugPanels: parseBool(process.env.REACT_APP_ADMIN_ENABLE_DEBUG_PANELS, false),
    enableInactivityTimeout: parseBool(process.env.REACT_APP_ADMIN_ENABLE_INACTIVITY_TIMEOUT, true),
    // US19: Feature flag to enable/disable Hedge Mode UI in admin
    enableHedgeModeUI: parseBool(process.env.REACT_APP_ADMIN_ENABLE_HEDGE_MODE_UI, false),
    /** Major Crypto Pairs (CRYPTO_PAIRS) — mirrors trading.crypto-pairs.enabled */
    enableCryptoPairs: parseBool(process.env.REACT_APP_ADMIN_ENABLE_CRYPTO_PAIRS, false),
  },
  auth: {
    sessionTimeoutMinutes: parseNumber(process.env.REACT_APP_AUTH_SESSION_TIMEOUT_MINUTES, 60),
    jwtExpiryMinutes: parseNumber(process.env.REACT_APP_AUTH_JWT_EXPIRY_MINUTES, 15),
    refreshThresholdMinutes: parseNumber(process.env.REACT_APP_AUTH_REFRESH_THRESHOLD_MINUTES, 5),
    retailSessionTimeoutMinutes: parseNumber(process.env.REACT_APP_RETAIL_SESSION_TIMEOUT_MINUTES, 10),
    adminSessionTimeoutMinutes: parseNumber(process.env.REACT_APP_ADMIN_SESSION_TIMEOUT_MINUTES, 30),
  },
  general: {
    environment: process.env.REACT_APP_ENVIRONMENT || 'development',
    enableLogging: parseBool(process.env.REACT_APP_ENABLE_LOGGING, false),
    apiUrl: process.env.REACT_APP_API_URL || getApiBaseUrl(),
    wsUrl: process.env.REACT_APP_WS_URL || getWsBaseUrl(),
    /** Shared with Java security.client-gate.* — required when gate is enabled */
    clientRetailKey: process.env.REACT_APP_CLIENT_RETAIL_KEY || 'zogzila-retail-portal-change-me-7f3a9c2e',
    clientAdminKey: process.env.REACT_APP_CLIENT_ADMIN_KEY || 'zogzila-admin-portal-change-me-b1d84e6a',
  },
};

// Context detection utilities
export const featureFlagsUtils = {
  // Check if we're in admin context (you can enhance this based on your routing)
  isAdminContext: (): boolean => {
    return window.location.pathname.startsWith('/admin');
  },

  // Get current context flags
  getCurrentContextFlags: () => {
    return featureFlagsUtils.isAdminContext() ? featureFlags.admin : featureFlags.retail;
  },

  // Check if a feature is enabled for current context
  isFeatureEnabled: (featureName: keyof typeof featureFlags.retail): boolean => {
    // This helper is scoped to retail feature keys only.
    // Using retail flags directly avoids union-indexing issues with admin-only keys.
    return featureFlags.retail[featureName] ?? false;
  },
};

// React Hook for feature flags
export const useFeatureFlags = () => {
  const isAdmin = featureFlagsUtils.isAdminContext();
  const flags = isAdmin ? featureFlags.admin : featureFlags.retail;
  const contextFlags: Partial<Record<keyof typeof featureFlags.retail, boolean>> = isAdmin
    ? featureFlags.admin
    : featureFlags.retail;

  return {
    flags,
    isAdmin,
    isEnabled: (featureName: keyof typeof featureFlags.retail) => contextFlags[featureName] ?? false,
    isAdminContext: featureFlagsUtils.isAdminContext,
    auth: featureFlags.auth,
    general: featureFlags.general,
  };
};

// Debug function to log current configuration
export const debugFeatureFlags = () => {
  if (featureFlags.general.enableLogging) {
    console.log('🔧 Feature Flags Configuration');
    console.log('Environment:', featureFlags.general.environment);
    console.log('Current Context:', featureFlagsUtils.isAdminContext() ? 'Admin' : 'Retail');
    console.log('Development Mode:', featureFlags.general.environment === 'development');
    console.log('Retail Flags:', featureFlags.retail);
    console.log('Admin Flags:', featureFlags.admin);
    console.log('Auth Config:', featureFlags.auth);
  }
};

// Auto-debug in development
if (featureFlags.general.environment === 'development' && featureFlags.general.enableLogging) {
  debugFeatureFlags();
}

export default featureFlags; 